import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  delay,
  exhaustMap,
  from,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { DnaSkinFaceScanActionType } from '@interfaces/schema';
import { Session } from '@providers/session/session.service';
import { ReportSkinHealthGuidelineActions } from '../health-guideline.actions';
import { ReportSkinHealthGuidelineTakePictureActions } from '../take-picture/take-picture.actions';
import { selectReportSkinHealthGuidelineTakePictureUploadImageResponse } from '../take-picture/take-picture.selectors';
import { ReportSkinHealthGuidelineAnalysisActions } from './analysis.actions';
import { selectReportSkinHealthGuidelineAnalysisFaceAnalysisResponse } from './analysis.selectors';
import { ReportSkinHealthGuidelineAnalysisService } from './analysis.service';

/**
 * Report Skin Health Guideline Analysis Effects.
 *
 * @exports
 * @class ReportSkinHealthGuidelineAnalysisEffects
 */
@Injectable()
export class ReportSkinHealthGuidelineAnalysisEffects extends ReportSkinHealthGuidelineAnalysisService {
  private readonly actions = inject(Actions);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  // Effects for handling the submission of face analysis results
  readonly faceAnalyzeSubmitResult = createEffect(
    () =>
      this.actions.pipe(
        ofType(
          ReportSkinHealthGuidelineAnalysisActions.submitResultFaceAnalysis
        ),
        concatLatestFrom(() => [
          this.store.select(
            selectReportSkinHealthGuidelineAnalysisFaceAnalysisResponse
          )
        ]),
        switchMap(([, faceAnalysisResponse]) => {
          const id = faceAnalysisResponse?.data?.id as string;
          if (!id) {
            return of(
              ReportSkinHealthGuidelineAnalysisActions.setLoadingSubmission({
                loadingSubmission: false
              })
            );
          }
          return this.faceAnalyzeSumbmitResult(id).pipe(
            mergeMap((value) => [
              ReportSkinHealthGuidelineAnalysisActions.submitResultFaceAnalysisStatus(
                {
                  status: value
                }
              ),
              ReportSkinHealthGuidelineAnalysisActions.setLoadingSubmission({
                loadingSubmission: false
              })
            ]),
            startWith(
              ReportSkinHealthGuidelineAnalysisActions.setLoadingSubmission({
                loadingSubmission: true
              })
            )
          );
        })
      ),
    { dispatch: true }
  );

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => ReportSkinHealthGuidelineAnalysisActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onInitiateFaceAnalysisSubscription = createEffect(
    () =>
      this.actions.pipe(
        ofType(
          ReportSkinHealthGuidelineAnalysisActions.initiateFaceAnalysisSubscription
        ),
        exhaustMap(() =>
          this.faceAnalysisProcess().pipe(
            mergeMap((response) => {
              let actionsToDispatch: Array<any> = [
                ReportSkinHealthGuidelineAnalysisActions.faceAnalysisSubscriptionResponse(
                  {
                    data: response
                  }
                )
              ];

              // replace the latest face analyze process id in session storage
              this.session.store(
                this.session.keys.idLatestDataSkinFaceAnalyzeProcess,
                response.data?.id
              );

              if (
                response.action === DnaSkinFaceScanActionType.Completed ||
                response.action === DnaSkinFaceScanActionType.Failed
              ) {
                actionsToDispatch.push(
                  ReportSkinHealthGuidelineAnalysisActions.setLoading({
                    loading: false
                  })
                );
              }
              return from(actionsToDispatch);
            }),
            // Handle errors from the subscription stream
            catchError((error) => {
              // eslint-disable-next-line no-console
              console.error(
                '[initiateSubscription] GraphQL Subscription Error:',
                error
              );
              return of(
                ReportSkinHealthGuidelineAnalysisActions.setLoading({
                  loading: false
                })
              );
            }),
            // takeUntil stops the subscription stream when 'stopFaceAnalysisSubscription' is dispatched
            takeUntil(
              this.actions.pipe(
                ofType(
                  ReportSkinHealthGuidelineAnalysisActions.stopFaceAnalysisSubscription
                )
              )
            ),
            startWith(
              ReportSkinHealthGuidelineAnalysisActions.setLoading({
                loading: true
              })
            )
          )
        )
      ),
    { dispatch: true }
  );

  readonly onRetakePicture = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ReportSkinHealthGuidelineAnalysisActions.retakePicture),
        delay(1500),
        mergeMap(() => [
          ReportSkinHealthGuidelineAnalysisActions.reset(),
          ReportSkinHealthGuidelineTakePictureActions.reset(),
          ReportSkinHealthGuidelineActions.reset()
        ])
      );
    },
    { dispatch: true }
  );

  readonly onSubmitProcessFaceAnalysis = createEffect(
    () => {
      return this.actions.pipe(
        ofType(
          ReportSkinHealthGuidelineAnalysisActions.submitProcessFaceAnalysis
        ),
        concatLatestFrom(() => [
          this.store.select(
            selectReportSkinHealthGuidelineTakePictureUploadImageResponse
          )
        ]),
        mergeMap(([, uploadImageResponse]) => {
          const id = uploadImageResponse?.data?.id;
          if (!id) {
            return of(
              ReportSkinHealthGuidelineAnalysisActions.setLoading({
                loading: false
              })
            );
          }
          return this.faceAnalyze(id).pipe(
            mergeMap((response) => [
              ReportSkinHealthGuidelineAnalysisActions.submitProcessFaceAnalysisStatus(
                { status: response }
              ),
              ReportSkinHealthGuidelineAnalysisActions.setLoading({
                loading: false
              })
            ]),
            startWith(
              ReportSkinHealthGuidelineAnalysisActions.setLoading({
                loading: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );
}
